import React, { useMemo } from "react";
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Tooltip,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import BlockIcon from "@mui/icons-material/Block";
import DrawerHeader, { DRAWER_NO_SLIDE, DRAWER_PAPER_PROPS } from "./DrawerHeader";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import WarningIcon from "@mui/icons-material/Warning";
import CircleIcon from '@mui/icons-material/Circle';
import { estPayant } from "../utils/estPayant";


const corrigerOrthographeUpdateMode = (mode) => {
  return mode.replace(/(manuel(le)?|le)/gi, "Manuelle");
};

const isOutdated = (version, latestVersion ) => {
  
  if (!version || !latestVersion) return false;

  if (typeof version !== 'string' || typeof latestVersion !== 'string') return false;

  const currentMatch = version.match(/^(\d+)\.(\d+)/);
  const latestMatch = latestVersion.match(/^(\d+)\.(\d+)/);

  if (!currentMatch || !latestMatch) return false;


  const currentMajor = parseInt(currentMatch[1]);
  const currentMinor = parseInt(currentMatch[2]);
  const latestMajor = parseInt(latestMatch[1]);
  const latestMinor = parseInt(latestMatch[2]);

  if (currentMajor < latestMajor) {
    return true;
} 
  if (currentMajor === latestMajor && (latestMinor - currentMinor > 4)) {
    return true;
  }

  return false;
  }


// Construit une fois, au lieu de rebalayer toute la liste CVE pour chaque ligne.
const buildAffectedIndex = (cveList) => {
  const slugs = new Set();
  const names = new Set();
  (Array.isArray(cveList) ? cveList : []).forEach((group) => {
    (group.plugins || []).forEach((plugin) => {
      if (plugin.slug) slugs.add(String(plugin.slug).trim().toLowerCase());
      if (plugin.name) names.add(String(plugin.name).trim().toLowerCase());
    });
  });
  return { slugs, names };
};

// Par slug d'abord, repli sur le nom exact. Un `includes` sur le nom donnait des faux
// positifs : « seo » matchait « wordpress-seo ».
const hasCVE = (plugin, affected) => {
  const slug = plugin?.slug ? String(plugin.slug).trim().toLowerCase() : null;
  if (slug && affected.slugs.has(slug)) return true;
  const name = plugin?.name ? String(plugin.name).trim().toLowerCase() : null;
  return !!name && affected.names.has(name);
};

const PluginDetailsDrawer = ({
  selectedFilteredIndex,
  handleCloseDrawer,
  filteredData,
  cveList,
  role,
}) => {
  const theme = useTheme();

  const affected = useMemo(() => buildAffectedIndex(cveList), [cveList]);

  if (selectedFilteredIndex === null || selectedFilteredIndex >= filteredData.length) {
    return null;
  }

  const selectedData = filteredData[selectedFilteredIndex];

  if (!selectedData) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={selectedFilteredIndex !== null && selectedFilteredIndex < filteredData.length}
      onClose={handleCloseDrawer}
      PaperProps={DRAWER_PAPER_PROPS.wide}
      SlideProps={DRAWER_NO_SLIDE}
      // Sans ça, le scroll lock de MUI pose overflow:hidden + padding-right sur
      // <body> à l'ouverture, ce qui relayoute tout le dashboard pendant l'animation.
      disableScrollLock
    >
      <div>
        {selectedFilteredIndex !== null && selectedFilteredIndex < filteredData.length && (
          <div>
            <DrawerHeader
              title={`${selectedData.content.name} ${selectedData.content.environnement}`}
              onClose={handleCloseDrawer}
              role={role}
            />
            <div className="drawerTab">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="left">Nom</TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version
                      </TableCell>
                      {role === "admin" && (
                        <>
                          <TableCell>Dernière version</TableCell>
                          <TableCell>Mise à jour</TableCell>
                        </>
                      )}
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Payant
                      </TableCell> 
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedData.content.plugins.map((plugin, i) => (
                      <TableRow
                        sx={{
                          backgroundColor: i % 2 === 0
                            ? theme.palette.background.alternate
                            : theme.palette.background.paper,
                        }}
                        key={plugin.name}
                      >
                        <TableCell align="center">
                          {plugin.active === 1 ? (
                            <DoneIcon style={{ color: "green" }} />
                          ) : (
                            <BlockIcon style={{ color: "red" }} />
                          )}
                        </TableCell>
                        <TableCell
                          style={{ width: "50%", fontWeight: "bold"}}
                          align="left"
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {plugin.name}
                            {isOutdated(plugin.version, plugin.last_version) === true &&(
                              <Tooltip title="Version obsolète" arrow>
                                <WarningIcon color="warning" style={{ fontSize: "1.2rem" }}/>
                              </Tooltip>
                            )
                            }
                            {hasCVE(plugin, affected) && (
                              <Tooltip title="CVE détectée" arrow>
                                <CircleIcon color="error" style={{ fontSize: "0.63rem" }}/>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          align="center"
                          style={{ fontWeight: "bold" }}
                        >
                          {plugin.version}
                        </TableCell>
                       
                        {role === "admin" && (
                          <>
                            <TableCell style={{ color: "#D50513" }}>
                              {plugin.last_version}
                            </TableCell>
                            <TableCell>
                              {corrigerOrthographeUpdateMode(plugin.updateModePLUG)}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="center" style={{ fontWeight: "bold" }}>
                          {estPayant(plugin) ? <FontAwesomeIcon icon={faDollarSign} /> : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div className="drawerTab__themes">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="left">Nom du thème</TableCell>
                      <TableCell align="center" style={{ fontWeight: "bold" }}>
                        Version
                      </TableCell>
                      <TableCell align="center">Parent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedData.content.themes.map((themeItem, i) => (
                      <TableRow
                        sx={{
                          backgroundColor: i % 2 === 0
                            ? theme.palette.background.alternate
                            : theme.palette.background.paper,
                        }}
                        key={themeItem.name}
                      >
                        <TableCell align="center">
                          {themeItem.active === 1 ? (
                            <DoneIcon style={{ color: "green" }} />
                          ) : (
                            <BlockIcon style={{ color: "red" }} />
                          )}
                        </TableCell>
                        <TableCell
                          style={{ width: "50%", fontWeight: "bold" }}
                          align="left"
                        >
                          {themeItem.name}
                        </TableCell>
                        <TableCell
                          align="center"
                          style={{ fontWeight: "bold" }}
                        >
                          {themeItem.version}
                        </TableCell>
                        <TableCell align="center">{themeItem.parent}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default PluginDetailsDrawer;
